import { mapMutations, mapState } from 'vuex';
import iconsMixin from '../state/bridge/iconsMixin';
import ApiService from '@/services/apiService';
import { downloadAttachment, isNullOrEmpty } from '@/utils/vuex';
import get from 'lodash/get';
import cloneDeep from 'lodash/cloneDeep';
import clone from 'lodash/clone';
import pullAt from 'lodash/pullAt';
import uniq from 'lodash/uniq';
import chunk from 'lodash/chunk';
const MAX_COPIES = 100;
export default {
  name: 'CoreDtwinDuplicateBase',
  mixins: [iconsMixin],
  props: {
    orgTwin: {
      type: Object,
      required: true
    },
    orgTwinStates: {
      type: Array,
      required: true
    },
    isOpen: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      twinCreateFormValid: false,
      globalParentId: false,
      globalParentTwin: {},
      metaInfoToggles: {
        tags: true,
        address: false,
        geocoordinates: false,
        keyvalue: false,
        referenceIds: false
      },
      copiesCount: 1,
      statePickerMode: 'virtualdevices',
      twinCopiesData: [],
      errornousParentId: -1,
      loadingStates: false,
      bridgeConfigs: [],
      // only for use in text-field validator
      maxCopies: MAX_COPIES
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color']),
    bridgeTypeMap() {
      const map = {};
      if (this.bridgeTypes) {
        this.bridgeTypes.forEach(type => {
          map[type.type] = type;
        });
      }
      return map;
    },
    pickableStates() {
      return this.orgTwinStates.filter(state => {
        let validSourceTypes = [];
        if (this.statePickerMode === 'iothub') {
          validSourceTypes = ['iothub', 'iothub-aggregate'];
        } else if (this.statePickerMode === 'complete') {
          validSourceTypes = ['iothub', 'iothub-aggregate', 'bridge'];
        } else if (this.statePickerMode === 'virtualdevices') {
          validSourceTypes = ['iothub', 'iothub-aggregate', 'virtual-device', 'virtual-device-aggregate'];
        }
        return validSourceTypes.indexOf(state.properties.binding.type) >= 0;
      });
    }
  },
  watch: {
    copiesCount() {
      this.syncCopiesData();
    },
    orgTwin() {
      this.loadBridgeData();
    }
  },
  created() {
    this.loadBridgeData();
    this.syncCopiesData();
  },
  methods: {
    ...mapMutations('app', ['snackbar']),
    get,
    getDefaultTags() {
      return cloneDeep(this.orgTwin.data.tags || []);
    },
    getDefaultKeyValue() {
      return cloneDeep(this.orgTwin.data.keyValue || {});
    },
    loadBridgeData() {
      this.loadingStates = true;
      Promise.all([ApiService.getBridgeTypes(), ApiService.getBridgesForTenant(this.orgTwin.tenantId)]).then(configData => {
        const types = configData[0];
        const configs = configData[1];
        this.bridgeConfigs = configs.filter(config => this.bridgeConfig(config, types).manifest.functionScope.invokeDirection.inbound).map(config => {
          return {
            type: config.type,
            image: this.bridgeTypesIcons[config.type],
            title: config.name,
            configId: config.id,
            tenantId: config.tenantId
          };
        });
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.twinScopeCreationFailedSnack'), 'error');
        }
        console.error(error);
      }).finally(() => {
        this.syncCopiesData();
        this.loadingStates = false;
      });
    },
    bridgeConfig(bridge, types) {
      if (types && Array.isArray(types)) {
        const tempMap = {};
        types.forEach(type => {
          tempMap[type.type] = type;
        });
        return tempMap[bridge.type];
      }
      return this.bridgeTypeMap[bridge.type];
    },
    syncCopiesData() {
      // during processing, put all into separate array
      let syncedData = clone(this.twinCopiesData);
      while (syncedData.length < Math.min(this.maxCopies, this.copiesCount)) {
        syncedData.push({
          id: this.twinCopiesData.length,
          parentId: this.orgTwin.parentId,
          tenantId: this.orgTwin.tenantId,
          title: `${this.orgTwin.title} copy ${syncedData.length + 1}`,
          data: {
            faIcon: this.orgTwin.data.faIcon,
            tags: this.getDefaultTags(),
            keyValue: this.getDefaultKeyValue(),
            address: {
              formatted_address: ''
            }
          },
          coordinates: {
            coordinates: [null, null]
          },
          referenceId: this.orgTwin['dynamical-data-routing-destination']
        });
      }
      syncedData.forEach(twin => {
        if ((twin.states || []).length === 0) {
          twin.states = cloneDeep(this.orgTwinStates);
        }
      });
      while (syncedData.length > Math.min(this.maxCopies, this.copiesCount)) {
        pullAt(syncedData, [syncedData.length - 1]);
      }
      // at the very last, copy it in the target object
      this.twinCopiesData = syncedData;
    },
    twinParentFilter(twin) {
      return !get(twin, 'properties.disabled');
    },
    updateTwinsParents(parentTwin) {
      this.globalParentTwin = parentTwin;
      this.$refs['duplicate-config-form'].updateTwinsParents(parentTwin);
    },
    changedDisplayOptions() {
      this.metaInfoToggles = cloneDeep(this.metaInfoToggles);
    },
    cloneOrgTwinStates() {
      this.$emit('forceRefreshStates');
      this.$refs['createForm'].validate();
    },
    randomHexString(len) {
      let charSet = 'ABCDEF0123456789';
      let randomString = '';
      for (let i = 0; i < len; i++) {
        let randomPoz = Math.floor(Math.random() * charSet.length);
        randomString += charSet.substring(randomPoz, randomPoz + 1);
      }
      return randomString;
    },
    downloadSampleCsv() {
      const tagsSample = this.getDefaultTags();
      const coordinateSample = get(this.orgTwin, 'coordinates.coordinates') || [null, null];
      const addressSample = get(this.orgTwin, 'data.address.formatted_address') || '';
      let titleCols = ['title', 'parentId', 'tags', 'longitude', 'latitude', 'address', 'keyvalue', 'referenceId'].concat(this.pickableStates.map(state => state.key));
      const pickableStatesDemoData = this.pickableStates.map(s => {
        if (s.properties.binding.type.startsWith('iothub')) {
          return this.randomHexString(16);
        } else {
          return `EXT_ID_${this.randomHexString(16)}`;
        }
      });
      const keyvalueDemoDataObj = Object.keys(this.orgTwin.data.keyValue || {}).length === 0 ? {
        key1: 'value1'
      } : this.orgTwin.data.keyValue;
      const keyvalueDemoData = Object.keys(keyvalueDemoDataObj).map(key => {
        return `${key}: ${keyvalueDemoDataObj[key]}`;
      });
      const ddrDestinationDemoDataArray = (this.orgTwin['dynamical-data-routing-destination'] || []).length === 0 ? [{
        reference1: 'value1'
      }] : this.orgTwin['dynamical-data-routing-destination'];
      const ddrDestinationDemoData = ddrDestinationDemoDataArray.map(entry => {
        const key = Object.keys(entry)[0];
        const value = entry[key];
        return `${key}: ${value}`;
      });
      const colSeparator = ',';
      const csvData = [`${titleCols.join(colSeparator)}`, `${[
      // cannot have a comma in the title screw up all indices; the user probably won't know what is going on either
      `"${this.orgTwin.title}"`, this.orgTwin.parentId, `"${tagsSample.join(colSeparator)}"`, coordinateSample[0], coordinateSample[1], addressSample === '' ? '' : `"${addressSample}"`, `"${keyvalueDemoData.join(colSeparator)}"`, `"${ddrDestinationDemoData.join(colSeparator)}"`].join(colSeparator)}${colSeparator}${pickableStatesDemoData.join(colSeparator)}`].join('\n');
      const fileName = 'bulk-create-twins.csv';
      downloadAttachment('text/plain;charset=utf-8', csvData, fileName);
    },
    notifyInvalidCsvImport() {
      this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.copy.invalidCsvSnack'), 'error');
    },
    // eslint-disable-next-line max-statements
    async uploadedFile(csv) {
      let lines = csv.split('\n');
      if (lines.length <= 1) {
        this.notifyInvalidCsvImport();
        return;
      }

      // Return array of string values, or NULL if CSV string not well formed, loosely based on RFC 4180
      // https://stackoverflow.com/a/8497474
      // https://www.rfc-editor.org/rfc/rfc4180
      function csvToArray(text, separator = ',') {
        // adapted to support either comma or semilicon as separators
        // had to do a lot of ugly escaping, so it looks even more horrid than the original Regex
        // eslint-disable-next-line max-len
        const re_valid = new RegExp(`^\\s*(?:'[^'\\\\]*(?:\\\\[\\S\\s][^'\\\\]*)*'|"[^"\\\\]*(?:\\\\[\\S\\s][^"\\\\]*)*"|[^${separator}'"\\s\\\\]*(?:\\s+[^${separator}'"\\s\\\\]+)*)\\s*(?:${separator}\\s*(?:'[^'\\\\]*(?:\\\\[\\S\\s][^'\\\\]*)*'|"[^"\\\\]*(?:\\\\[\\S\\s][^"\\\\]*)*"|[^${separator}'"\\s\\\\]*(?:\\s+[^${separator}'"\\s\\\\]+)*)\\s*)*$`);
        const re_value = new RegExp(`(?!\\s*$)\\s*(?:'([^'\\\\]*(?:\\\\[\\S\\s][^'\\\\]*)*)'|"([^"\\\\]*(?:\\\\[\\S\\s][^"\\\\]*)*)"|([^${separator}'"\\s\\\\]*(?:\\s+[^${separator}'"\\s\\\\]+)*))\\s*(?:${separator}|$)`, 'g');
        // Return NULL if input string is not well formed CSV string.
        if (!re_valid.test(text)) {
          return null;
        }
        var a = []; // Initialize array to receive values.
        text.replace(re_value,
        // "Walk" the string using replace with callback.
        function (m0, m1, m2, m3) {
          // Remove backslash from \' in single quoted values.
          if (m1 !== undefined) {
            a.push(m1.replace(/\\'/g, "'"));
          } else if (m2 !== undefined) {
            // Remove backslash from \" in double quoted values.
            a.push(m2.replace(/\\"/g, '"'));
          } else if (m3 !== undefined) {
            a.push(m3);
          }
          return ''; // Return empty string.
        });
        // Handle special case of empty last value.
        if (new RegExp(`${separator}\\s*$`).test(text)) {
          a.push('');
        }
        return a;
      }
      function handleInnerStringSplit(inputString, colSeparator, tagSeparator) {
        let usedSplitted = inputString.split(tagSeparator);
        if (usedSplitted.length <= 1) {
          const otherTagSeparator = inputString.split(colSeparator);
          if (otherTagSeparator.length > usedSplitted.length) {
            usedSplitted = otherTagSeparator;
          }
        }
        return usedSplitted;
      }
      let colSeparator = ',';
      let tagSeparator = /[;|]/;
      let newTwinCopiesData = [];
      const commaHeader = csvToArray(lines[0].trim(), ',') || [];
      const semicolonHeader = csvToArray(lines[0].trim(), ';') || [];
      let header = commaHeader;
      if (semicolonHeader.length > commaHeader.length) {
        colSeparator = ';';
        tagSeparator = /[,|]/;
        header = semicolonHeader;
      }
      if (header.length === 0) {
        this.notifyInvalidCsvImport();
        return;
      }
      const trimmedHeader = header.map(key => key.trim());
      const normalizedHeader = trimmedHeader.map(key => key.toLowerCase());
      pullAt(lines, [0]);
      let n1Euis = [];
      let n2VDeviceDetails = [];
      // eslint-disable-next-line max-statements
      lines.forEach(line => {
        const parts = csvToArray(line.trim(), colSeparator);
        if (!parts || parts.length < trimmedHeader.length || newTwinCopiesData.length >= this.maxCopies) {
          // not a complete data-set, ignore current line
          return;
        }
        const copiedStates = cloneDeep(this.orgTwinStates);
        copiedStates.forEach(state => {
          // extract info from CSV for configurable states, aka iot-hub or virtual-devices
          // load all their data for UI-side verification
          const pickableState = this.pickableStates.find(s => s.key === state.key);
          if (!pickableState) {
            return;
          }
          // use "lastIndexOf" rather than "findIndex" in case that states are named "latitude"/"longitude"
          // since those are in the original CSV already for the coordinates, do not confuse the script
          const stateCsvIndex = trimmedHeader.lastIndexOf(state.key);
          if (stateCsvIndex >= 0) {
            const csvContent = parts[stateCsvIndex];
            if (pickableState.properties.binding.type.startsWith('iothub')) {
              // #### iothub states
              state.properties.binding.options.eui = csvContent;
              n1Euis.push(csvContent);
            } else if (pickableState.properties.binding.type.startsWith('virtual-device')) {
              // special cases to overwrite the state-source-type via CSV
              if (csvContent.toLowerCase() === 'none') {
                state.properties.binding.type = 'none';
                state.properties.vdExternalId = `${this.$i18n_t('DigitalTwin.DigitalTwin.states.copy.sourceType')}: ${this.$i18n_t('DigitalTwin.DigitalTwin.states.dataSources.none')}`;
              } else if (csvContent.toLowerCase() === 'null') {
                state.properties.binding.type = 'no-copy';
                state.properties.vdExternalId = this.$i18n_t('DigitalTwin.DigitalTwin.copy.noStateCopy');
              } else {
                // #### virtual device states
                if (!get(state, 'properties.binding.options.virtualDevice')) {
                  state.properties.binding.options = {
                    ...state.properties.binding.options,
                    virtualDevice: {}
                  };
                }
                const fetchOptions = this.$refs['duplicate-config-form'].fetchOptionsFromVDState(pickableState);
                fetchOptions.id = csvContent;
                state.properties.binding.options.virtualDevice.vdExternalId = fetchOptions.id;
                n2VDeviceDetails.push(fetchOptions);
              }
            }
          }
        });
        const titleIndex = normalizedHeader.indexOf('title');
        const parentIndex = normalizedHeader.indexOf('parentid');
        const tagsIndex = normalizedHeader.indexOf('tags');
        const usedTags = tagsIndex >= 0 ? handleInnerStringSplit(parts[tagsIndex].trim(), colSeparator, tagSeparator).map(tag => tag.trim()).filter(tag => tag !== '') : null;
        if (usedTags && usedTags.length > 0) {
          this.metaInfoToggles.tags = true;
        }
        const csvTitle = titleIndex >= 0 ? parts[titleIndex].trim() : '';
        const csvParentId = parentIndex >= 0 ? parseInt(parts[parentIndex].trim()) : NaN;
        const twinCopyData = {
          id: newTwinCopiesData.length,
          title: csvTitle === '' ? undefined : csvTitle,
          parentId: csvParentId || this.orgTwin.parentId,
          tenantId: this.orgTwin.tenantId,
          data: {
            faIcon: this.orgTwin.data.faIcon,
            tags: usedTags || this.getDefaultTags(),
            keyValue: this.getDefaultKeyValue(),
            address: {
              formatted_address: ''
            }
          },
          'dynamical-data-routing-destination': this.orgTwin['dynamical-data-routing-destination'],
          coordinates: {
            coordinates: [null, null]
          },
          states: copiedStates
        };
        if (trimmedHeader.length > 2) {
          // extract geo-data
          const latIndex = normalizedHeader.findIndex(key => key.startsWith('lat'));
          const lonIndex = normalizedHeader.findIndex(key => key.startsWith('lon'));
          const addressIndex = normalizedHeader.findIndex(key => key.startsWith('address'));
          if (latIndex >= 0 && lonIndex >= 0) {
            if (!isNaN(parseFloat(parts[lonIndex])) && !isNaN(parseFloat(parts[latIndex]))) {
              twinCopyData.coordinates = {
                coordinates: [parseFloat(parts[lonIndex]), parseFloat(parts[latIndex])]
              };
              this.metaInfoToggles.geocoordinates = true;
              // reset the polygon, in order for the backend to create a default-polygon around the coordinates
              twinCopyData.polygon = null;
            }
          }
          if (addressIndex >= 0) {
            twinCopyData.data.address = {
              // make the address nice again, by inserting commas where the alternative-separators were
              formatted_address: handleInnerStringSplit(parts[addressIndex].trim(), colSeparator, tagSeparator).join(', ')
            };
            this.metaInfoToggles.address = true;
          } else {
            twinCopyData.data.address = {
              formatted_address: ''
            };
          }
          let hasKeyValueData = false;
          // extract key-value data
          const keyValueIndex = normalizedHeader.findIndex(key => key === 'keyvalue');
          if (keyValueIndex >= 0) {
            twinCopyData.data.keyValue = {};
            const keyValueParts = handleInnerStringSplit(parts[keyValueIndex].trim(), colSeparator, tagSeparator);
            keyValueParts.forEach(kv => {
              const kvParts = kv.trim().split(':');
              if (kvParts.length >= 2) {
                twinCopyData.data.keyValue[kvParts[0].trim()] = kvParts[1].trim();
                hasKeyValueData = true;
              }
            });
          }
          if (hasKeyValueData) {
            this.metaInfoToggles.keyvalue = true;
          }
          let hasReferenceIdData = false;
          // extract reference id's
          const referenceIdsIndex = normalizedHeader.findIndex(key => key === 'referenceid' || key === 'referenceids');
          if (referenceIdsIndex >= 0) {
            twinCopyData['dynamical-data-routing-destination'] = [];
            const referenceIdsParts = handleInnerStringSplit(parts[referenceIdsIndex].trim(), colSeparator, tagSeparator);
            referenceIdsParts.forEach(kv => {
              const refIdsParts = kv.trim().split(':');
              if (refIdsParts.length >= 2) {
                const key = refIdsParts[0].trim();
                const value = refIdsParts[1].trim();
                twinCopyData['dynamical-data-routing-destination'].push({
                  [key]: value
                });
                hasReferenceIdData = true;
              }
            });
          }
          if (hasReferenceIdData) {
            this.metaInfoToggles.referenceIds = true;
          }
        }
        newTwinCopiesData.push(twinCopyData);
      });
      this.twinCopiesData = newTwinCopiesData;
      this.copiesCount = newTwinCopiesData.length;
      this.loadingStates = true;
      await this.fetchIotHubEuis(n1Euis);
      if (this.statePickerMode === 'virtualdevices') {
        await this.fetchVirtualDeviceDetails(n2VDeviceDetails);
      }
      this.loadingStates = false;
      this.cloneOrgTwinStates();
    },
    async fetchIotHubEuis(n1Euis) {
      // TODO: would be noice to search for multiple EUIs at once (comma-separated?)
      // for now, chunk the unique EUIs in buckets of 10 and fire 10 requests at once at a time
      const fetchEuis = chunk(uniq(n1Euis), 10);
      for (let i = 0; i < fetchEuis.length; i++) {
        await Promise.all(fetchEuis[i].map(eui => {
          return this.$refs['duplicate-config-form'].fetchIotHubDeviceDetails(eui);
        }));
      }
      await Promise.all(this.twinCopiesData.map(async twin => {
        await Promise.all(twin.states.map(state => {
          // all EUI-search-results are cached, so now wire the states up with their result
          return this.$refs['duplicate-config-form'].fetchIotHubDetails(state, twin, false);
        }));
        twin.states = cloneDeep(twin.states);
      }));
    },
    async fetchVirtualDeviceDetails(n2VDeviceDetails) {
      const fetchDetails = chunk(uniq(n2VDeviceDetails), 10);
      for (let i = 0; i < fetchDetails.length; i++) {
        await Promise.all(fetchDetails[i].map(details => {
          return this.$refs['duplicate-config-form'].fetchVirtualDeviceDataDetails(details);
        }));
      }
      await Promise.all(this.twinCopiesData.map(async twin => {
        await Promise.all(twin.states.map(state => {
          // all search-results are cached, so now wire the states up with their result
          return this.$refs['duplicate-config-form'].fetchVirtualDeviceData(state, twin, false);
        }));
        twin.states = cloneDeep(twin.states);
      }));
    },
    createCopies() {
      let twinCreationData = cloneDeep(this.twinCopiesData);
      twinCreationData.forEach(twin => {
        if (!this.metaInfoToggles.tags) {
          twin.data.tags = [];
        }
        if (!this.metaInfoToggles.keyvalue) {
          twin.data.keyValue = {};
        }
        if (isNullOrEmpty(get(twin, 'coordinates.coordinates[0]')) || isNullOrEmpty(get(twin, 'coordinates.coordinates[1]'))) {
          twin.coordinates = null;
        }
        twin.states = twin.states.map(state => {
          // do not persist temp form details
          delete state.deviceTitle;
          delete state.properties.binding.options.eui;
          delete state.properties.vdExternalId;

          // cleanup virtual-device data
          if (get(state, 'properties.binding.options.virtualDevice.vDeviceId')) {
            // first copy data to its target place
            state.properties.binding.options.virtualDevice.id = parseInt(state.properties.binding.options.virtualDevice.vDeviceId);
            state.properties.binding.options.virtualDevice.stateId = parseInt(state.properties.binding.options.virtualDevice.vDeviceStateId);
            // then cleanup
            delete state.properties.binding.options.virtualDevice.vDeviceId;
            delete state.properties.binding.options.virtualDevice.vdExternalId;
            delete state.properties.binding.options.virtualDevice.vDeviceStateId;
            delete state.properties.binding.options.virtualDevice.vDeviceKey;
          }
          if (get(state, 'properties.binding.type') === 'none') {
            state.properties.binding = {
              type: 'none',
              options: {
                aggregateStateBindingIds: []
              }
            };
          }
          if (get(state, 'properties.binding.type') === 'no-copy') {
            state.properties.binding.type = 'none';
            // mark as state that should not be copied
            // we can not simply filter the state out, as the duplicate feature will just copy a state as-is if not specified
            // as we do not want to introduce a new binding-type 'no-copy' in the backend for validation,
            // use this flag instead to filter out this state on backend-side
            state.properties.doNotCopy = true;
            state.properties.binding = {
              type: 'none',
              options: {
                aggregateStateBindingIds: []
              }
            };
          }
          state.title = state.properties.title;
          state.bindingConfig = state.properties.binding;
          // cleanup DB-attributes
          delete state.bindingIdentifier;
          delete state.bindingType;
          delete state.bindingVariable;
          delete state.configId;
          return state;
        });
      });
      ApiService.digitalTwinDuplicate(this.orgTwin.id, twinCreationData).then(() => {
        this.$snack(this.$i18n_t('BackgroundJobs.BackgroundJobs.snacks.jobStarted'), 'success');
        this.$emit('close');
      }).catch(error => {
        console.error('Error duplicating twin', error.response);
        if (error.code === 'ECONNABORTED') {
          this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.copy.timeoutSnack'), 'error');
          return;
        }
        this.$snack(this.$i18n_t('DigitalTwin.DigitalTwin.copy.failedSnack'), 'error');
        if (get(error, 'response.status') === 403 && get(error, 'response.data.data.twinId')) {
          this.errornousParentId = parseInt(get(error, 'response.data.data.twinId'));
          return;
        }
        if (get(error, 'response.status') === 404 && get(error, 'response.data.data.twinId')) {
          this.errornousParentId = parseInt(get(error, 'response.data.data.twinId'));
          return;
        }
      });
    }
  }
};